// -------------------- External Libraries --------------------
import { useEffect, useState, useCallback, useMemo, memo } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import _debounce from "lodash/debounce";
import { getStripePromise } from "~/helpers/stripe";
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { faCheck, faTimes, faLock } from "@fortawesome/pro-solid-svg-icons";

// -------------------- UI Components --------------------
import Modal from "~/components/ui/Modal";
import Button from "~/components/ui/Button";
import { SVG } from "~/components/ui/SVG";
import { Form } from "~/components/ui/Form";
import { toast } from "../toast";

// -------------------- Hooks --------------------
import { useForm } from "~/hooks/useForm";
import { useAccount } from "~/hooks/useAccount";
import { usePaymentMethod } from "~/hooks/usePaymentMethod";
import { useShopifyPayment } from "~/pages/checkout/useShopifyPayment";
import { useSetupIntent } from "~/pages/checkout/useSetupIntent";
import { useStripeConfirmation } from "~/pages/checkout/useStripeConfirmation";

// -------------------- API Queries --------------------
import { useUpdateCard, usePlans } from "~/api/billing/queries";

// -------------------- Helpers / Config --------------------
import handleErrors from "~/helpers/handleErrors";
import { triggerGTMAddPaymentInfo } from "~/helpers/gtm";
import { formConfig } from "~/pages/checkout/form";
import { getFeaturesTranslations } from "~/constants/features";

// -------------------- Types --------------------
import { IPlan, Limits } from "~/types/billing";

// -------------------- Assets --------------------
import FeaturesTestimonialImg from "~/assets/png/features-testimonial.png";
import LoaderSVG from "~/assets/svg/loader.svg?react";

// -------------------- Styles --------------------
import * as S from "./styles";

// -------------------- Constants --------------------
export const SHOW_PLAN_UPGRADE_MODAL = "showPlanUpgradeModal";

// -------------------- Event Trigger --------------------
export const triggerShowPlanUpgradeModal = (planId: string) => {
  console.log("Triggering plan upgrade modal for plan ID:", planId);
  window.dispatchEvent(
    new CustomEvent(SHOW_PLAN_UPGRADE_MODAL, { detail: { planId } })
  );
};

// -------------------- Props --------------------
interface Props {
  planId: string;
  hide: () => void;
}


// -------------------- Internal Component --------------------
const PlanUpgradeModalInternal = ({ planId, hide }: Props) => {
  const { t } = useTranslation();
  const { account, getAccount } = useAccount();
  const { data: plans } = usePlans();
  const { isUserShopify } = usePaymentMethod();

  const [isLoading, setIsLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>();

  const selectedPlan = plans?.find((plan: IPlan) => plan.id === planId);
  if (!selectedPlan) return null;

  const payShopify = useShopifyPayment(planId);
  const { mutateAsync: updateCard } = useUpdateCard();
  const cardForm = useForm(formConfig);

  const isAnnual = selectedPlan.interval === "annual";
  const currentPlan = account?.active_subscription?.plan;
  const isUpgrade =
    currentPlan && selectedPlan.cost_per_month > currentPlan.cost_per_month;
  const isDowngrade =
    currentPlan && selectedPlan.cost_per_month < currentPlan.cost_per_month;

  // -------------------- Handlers --------------------
  const handleShopifyPayment = async () => {
    const messages = {
      success: null,
      error: t("checkout.error"),
      loading: null,
    };
    const { response, errors } = await handleErrors(payShopify, messages);

    if (!errors) {
      localStorage.removeItem("onboarding_with_create_store");
    }

    if (response.confirmation_url) {
      window.location.replace(response.confirmation_url);
    }
  };

  const handleCardClear = () => setShowCardForm(false);

  const handleCardUpdate = async () => {
    const values = cardForm.getValues();
    const toastMessages = {
      loading: t("checkout.update_card"),
      success: t("checkout.update_success"),
      error: t("checkout.update_failed"),
    };

    if (showCardForm || !account?.stripe_card_digits) {
      const { errors } = await handleErrors(
        () => updateCard({ ...values }),
        toastMessages
      );
      if (!errors) {
        setShowCardForm(false);
        getAccount();
        triggerGTMAddPaymentInfo();
      }
    } else {
      setShowCardForm(true);
    }
  };

  // -------------------- Setup Intent --------------------
  const getSetupIntent = useSetupIntent();
  const getClientSecretDebounced = useCallback(
    _debounce(async (account) => {
      if (!clientSecret) {
        const response = await getSetupIntent(account);
        setClientSecret(response?.client_secret);
      }
    }, 200),
    [clientSecret]
  );

  useEffect(() => {
    if (account) {
      getClientSecretDebounced(account);
    }
  }, [account]);

  // -------------------- Checkout Stripe --------------------
  // In PlanUpgradeModalInternal component, update the CheckoutStripe component:

  const CheckoutStripe = memo(() => {
    const stripe = useStripe();
    const elements = useElements();
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [stripeReady, setStripeReady] = useState(false);

    const upgradeWithStore = useMemo(
      () => localStorage.getItem("onboarding_with_create_store") === "true",
      []
    );

    // Check if Stripe and Elements are ready
    useEffect(() => {
      if (stripe && elements) {
        // Add a small delay to ensure everything is properly initialized
        const timer = setTimeout(() => {
          setStripeReady(true);
        }, 200);
        return () => clearTimeout(timer);
      } else {
        setStripeReady(false);
      }
    }, [stripe, elements]);

    const { handlePayment: handleStripePayment } = useStripeConfirmation(
      selectedPlan,
      upgradeWithStore,
      setIsLoading,
      () => {
        hide();
        // Only show toast if we're in the modal context (not from URL redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const hasRedirectStatus =
          urlParams.get("redirect_status") === "succeeded";

        if (!hasRedirectStatus) {
          const action = isUpgrade
            ? "upgraded"
            : isDowngrade
              ? "downgraded"
              : "changed";
          toast.success(
            `Congratulations! Your plan was successfully ${action}`
          );
        }
      }
    );

    const handleClick = useCallback(async () => {
      // Prevent double clicks and ensure Stripe is ready
      if (isButtonLoading || !stripeReady) {
        return;
      }

      setIsButtonLoading(true);
      try {
        await handleStripePayment();
      } catch (error) {
        console.error("Payment failed:", error);
        toast.error(t("checkout.went_wrong"));
      } finally {
        setIsButtonLoading(false);
      }
    }, [isButtonLoading, stripeReady, handleStripePayment]);

    // Show different states based on what's happening
    const isProcessing = isLoading || isButtonLoading;
    const isInitializing = !stripeReady;

    // Show loader only when actually processing payment or initially loading
    if (isProcessing) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60px",
            marginTop: "30px",
          }}>
          <LoaderSVG />
        </div>
      );
    }

    return (
      <Button
        size="xl"
        bgColor="primary"
        color="white"
        fontWeight={600}
        onClick={handleClick}
        isDisabled={isInitializing || isProcessing}
        style={{
          marginTop: "30px",
          width: "100%",
          opacity: isInitializing ? 0.7 : 1,
          transition: "opacity 0.2s ease",
        }}>
        {isInitializing
          ? t("checkout.initializing", { defaultValue: "Initializing..." })
          : t("features.switch_to_plan", { planName: selectedPlan.name })}
      </Button>
    );
  });

  // Optimized CheckoutStripeWrapper with better state management
  const CheckoutStripeWrapper = memo(() => {
    const [elementsReady, setElementsReady] = useState(false);
    const [stripeInstance, setStripeInstance] = useState(null);

    // Memoize Stripe options to prevent unnecessary re-renders
    const options = useMemo(() => {
      if (!clientSecret) return undefined;
      return {
        clientSecret,
        appearance: { theme: "stripe" as const },
        // Add loader to prevent flash of unstyled content
        loader: "auto",
      };
    }, [clientSecret]);

    // Handle Stripe initialization
    useEffect(() => {
      if (clientSecret) {
        getStripePromise().then((stripe: any) => {
          setStripeInstance(stripe);
          // Small delay to ensure Elements are properly mounted
          setTimeout(() => {
            setElementsReady(true);
          }, 300);
        });
      }
    }, [clientSecret]);

    // Don't render anything until we have clientSecret
    if (!clientSecret) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60px",
            marginTop: "30px",
          }}>
          <LoaderSVG />
        </div>
      );
    }

    if (!options) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60px",
            marginTop: "30px",
          }}>
          <LoaderSVG />
        </div>
      );
    }
    return (
      // @ts-ignore
      <Elements options={options} stripe={getStripePromise()}>
        <div
          style={{
            opacity: elementsReady ? 1 : 0.5,
            transition: "opacity 0.3s ease",
          }}>
          <CheckoutStripe />
        </div>
      </Elements>
    );
  });

  // -------------------- UI Texts --------------------
  const texts = getFeaturesTranslations(
    t,
    Limits.ADS_WRITER,
    selectedPlan?.name ?? "",
    false
  );

  // -------------------- Render --------------------
  return (
    <Modal
      id="plan-limits"
      isShowing
      hide={hide}
      padding="24px">
      <S.IconContainer>
        <SVG icon={faLock} color="primary" size="xl" />
      </S.IconContainer>

      <S.Title>{texts.title}</S.Title>
      <S.Description>{texts.description}</S.Description>

      <S.TestimonialContainer>
        <S.TestimonialImage src={FeaturesTestimonialImg} />
        <S.TestimonialText>{texts.testimonial}</S.TestimonialText>
      </S.TestimonialContainer>

      {/* Card Form or Saved Card */}
      {showCardForm || !account?.stripe_card_digits ? (
        <S.CardEditContainer>
          <Form {...cardForm} />
          <S.ButtonContainer>
            <Button
              onClick={handleCardUpdate}
              bgColor="primaryLight"
              color="primary"
              isDisabled={!cardForm.isDirty}
              alignSelf="flex-end"
              fontWeight={600}>
              {t("checkout.save")}
            </Button>
            <Button
              onClick={handleCardClear}
              bgColor="primary"
              color="white"
              alignSelf="flex-end"
              fontWeight={600}>
              {t("dropshipping.cancel")}
            </Button>
          </S.ButtonContainer>
        </S.CardEditContainer>
      ) : (
        <S.SavedCardSection>
          <S.SavedCardTitle>{t("features.saved_card_title")}</S.SavedCardTitle>
          <S.SavedCardContainer>
            <S.CardDetails>
              <S.CardType>{t("features.credit_card")}</S.CardType>
              <S.CardNumber>{`**** **** **** ${account?.stripe_card_digits}`}</S.CardNumber>
            </S.CardDetails>
            <S.ChangeCardButton onClick={handleCardUpdate}>
              {t("features.change_card")}
            </S.ChangeCardButton>
          </S.SavedCardContainer>
        </S.SavedCardSection>
      )}

      {isUserShopify ? (
        <S.SwitchButton
          onClick={() => {
            hide();
            handleShopifyPayment();
          }}>
          {t("features.switch_to_plan", { planName: selectedPlan?.name })}
        </S.SwitchButton>
      ) : (
        <CheckoutStripeWrapper />
      )}

      <S.DisclaimerText>
        {t("features.no_fee_description", { planName: selectedPlan?.name })}
      </S.DisclaimerText>
    </Modal>
  );
};

// -------------------- Portal Wrapper --------------------
export const PlanUpgradeModal = () => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>();

  useEffect(() => {
    const handleEvent = ((e: CustomEvent<{ planId: string }>) =>
      setSelectedPlanId(e.detail.planId)) as EventListener;

    window.addEventListener(SHOW_PLAN_UPGRADE_MODAL, handleEvent);
    return () =>
      window.removeEventListener(SHOW_PLAN_UPGRADE_MODAL, handleEvent);
  }, []);

  if (!selectedPlanId) return null;

  return ReactDOM.createPortal(
    <PlanUpgradeModalInternal
      planId={selectedPlanId}
      hide={() => setSelectedPlanId(undefined)}
    />,
    document.body
  );
};
